import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    ChevronDown,
    ChevronRight,
    Phone,
    Mail,
    Search
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '../components/ui/collapsible';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalMembersDirectory() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [membersRes, groupsRes] = await Promise.all([
                axios.get(`${API_URL}/users`),
                axios.get(`${API_URL}/groups`)
            ]);
            setMembers(membersRes.data);
            setGroups(groupsRes.data);
            
            // Expand all groups by default
            const expanded = {};
            groupsRes.data.forEach(g => { expanded[g.id] = true; });
            expanded['unassigned'] = true;
            setExpandedGroups(expanded);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    // Filter members by search
    const filteredMembers = members.filter(member => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            member.first_name?.toLowerCase().includes(searchLower) ||
            member.last_name?.toLowerCase().includes(searchLower) ||
            member.email?.toLowerCase().includes(searchLower) ||
            member.username?.toLowerCase().includes(searchLower)
        );
    });

    // Group members by their group_id
    const membersByGroup = {};
    filteredMembers.forEach(member => {
        const groupId = member.group_id || 'unassigned';
        if (!membersByGroup[groupId]) membersByGroup[groupId] = [];
        membersByGroup[groupId].push(member);
    });

    // Sort members within each group alphabetically
    Object.keys(membersByGroup).forEach(groupId => {
        membersByGroup[groupId].sort((a, b) => {
            const nameA = `${a.last_name} ${a.first_name}`.toLowerCase();
            const nameB = `${b.last_name} ${b.first_name}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });
    });

    const getGroupName = (groupId) => {
        if (groupId === 'unassigned') return 'Unassigned Members';
        const group = groups.find(g => g.id === groupId);
        return group?.name || 'Unknown Group';
    };

    // Get ordered list of groups (with members first, then unassigned)
    const orderedGroupIds = [
        ...groups.filter(g => membersByGroup[g.id]?.length > 0).map(g => g.id),
        ...(membersByGroup['unassigned']?.length > 0 ? ['unassigned'] : [])
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <header className="bg-primary text-white h-14 flex items-center px-4">
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    <h1 className="text-lg font-semibold">Members Directory</h1>
                </header>
                <div className="p-4 space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-20 bg-muted rounded-lg animate-pulse"></div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="portal-members-directory">
            {/* Header */}
            <header className="bg-primary text-white sticky top-0 z-30">
                <div className="flex items-center h-14 px-4">
                    <button
                        onClick={() => navigate('/portal')}
                        className="mr-3 p-1 hover:bg-white/20 rounded"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-semibold">Members Directory</h1>
                </div>
            </header>

            {/* Search */}
            <div className="p-4 border-b border-border bg-card">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Search members..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-members"
                    />
                </div>
            </div>

            {/* Groups with Members */}
            <div className="divide-y divide-border">
                {orderedGroupIds.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                        <p>No members found</p>
                    </div>
                ) : (
                    orderedGroupIds.map((groupId) => {
                        const groupMembers = membersByGroup[groupId] || [];
                        const isExpanded = expandedGroups[groupId];
                        
                        return (
                            <Collapsible
                                key={groupId}
                                open={isExpanded}
                                onOpenChange={() => toggleGroup(groupId)}
                            >
                                <CollapsibleTrigger asChild>
                                    <button
                                        className="w-full px-4 py-3 flex items-center justify-between bg-muted/30 hover:bg-muted/50 transition-colors"
                                        data-testid={`group-header-${groupId}`}
                                    >
                                        <span className="font-medium text-sm">
                                            {getGroupName(groupId)} ({groupMembers.length})
                                        </span>
                                        {isExpanded ? (
                                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                        ) : (
                                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                        )}
                                    </button>
                                </CollapsibleTrigger>
                                
                                <CollapsibleContent>
                                    <div className="divide-y divide-border">
                                        {groupMembers.map((member) => (
                                            <button
                                                key={member.id}
                                                onClick={() => navigate(`/members/${member.id}`)}
                                                className="w-full p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left"
                                                data-testid={`member-${member.id}`}
                                            >
                                                <Avatar className="h-12 w-12">
                                                    <AvatarImage src={member.profile_image} />
                                                    <AvatarFallback className="bg-gray-500 text-white font-semibold">
                                                        {member.first_name?.[0]}{member.last_name?.[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium">
                                                        {member.first_name} {member.last_name}
                                                        {member.username && (
                                                            <span className="text-muted-foreground font-normal ml-2">
                                                                ({member.username})
                                                            </span>
                                                        )}
                                                    </p>
                                                    <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-foreground">
                                                        {member.phone && (
                                                            <span className="flex items-center gap-1">
                                                                <Phone className="w-3 h-3" />
                                                                {member.phone}
                                                            </span>
                                                        )}
                                                        {member.email && (
                                                            <span className="flex items-center gap-1">
                                                                <Mail className="w-3 h-3" />
                                                                {member.email}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                            </button>
                                        ))}
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        );
                    })
                )}
            </div>
        </div>
    );
}
